import type { AuditEntry, DeviceCapabilities, EnrollmentState, GroupJob, HopTestResult, Member } from "../cloud/api";
import { anyUnverified } from "../groups/forwarding";
import { hopTestPassed } from "../groups/hop-test";
import type { SignedManifestEnvelope } from "../images/manifest";
import type { PreflightCode } from "../models/preflight";

/**
 * Pure state machine for the install flow. Every transition is a reducer over events so
 * the whole thing is unit-testable and the state can be persisted verbatim (a reload in
 * the middle of a download comes back to the same step with the same manifest).
 *
 * v3 (line groups): step 1 picks or creates a group; after enrollment the phone is appended
 * to (or replaces a member of) the group and the page follows the cloud's chain-reprogramming
 * job, then runs the live hop test when needed. Removal is a side flow that needs no phone.
 */

export type Step = "signin" | "group" | "preflight" | "unlock" | "flash" | "enroll" | "member" | "hoptest" | "live" | "remove";
export type Mode = "install" | "reflash" | "stock";
/** What this phone becomes in the group: member 1 of a new group, the new last member, or a replacement. */
export type Intent = "create" | "add" | "replace";
export type FlashPhase = "manifest" | "download" | "flashing" | "locking" | "done";
/**
 * D18: QuickHub OS has no eSIM manager (Google's LPA is part of Play Services). A physical SIM
 * is unaffected; an eSIM profile must already be activated on stock Android before flashing —
 * it persists in the eUICC, but it cannot be added, switched or deleted until the phone is
 * restored to stock. The customer confirms one of these before the unlock step.
 */
export type SimReady = "physical" | "esim_activated";

export const STEP_ORDER: readonly Step[] = ["signin", "group", "preflight", "unlock", "flash", "enroll", "member", "hoptest", "live"];

export interface DeviceInfo {
  serial: string;
  product: string;
  unlocked: boolean;
  currentSlot: string | undefined;
  antiRollback: string | undefined;
  isUserspace: boolean;
  versionBootloader: string | undefined;
  versionBaseband: string | undefined;
  unlockAbility: boolean | undefined;
}

export interface PreflightSummary {
  ok: boolean;
  code: PreflightCode;
  warnings: PreflightCode[];
  carrierHint: string | undefined;
  modelName: string | undefined;
}

export interface FlashProgress {
  phase: FlashPhase;
  fileIndex: number;
  fileCount: number;
  bytesDone: number;
  bytesTotal: number;
  /** Partition or file currently in progress. */
  current: string | undefined;
  /** Bytes of the current partition written so far (flashing phase). */
  partDone: number;
  partTotal: number;
}

export interface GroupInfo {
  id: string;
  name: string;
  mainNumber: string;
  memberCount: number;
}

export interface EnrollmentInfo {
  id: string;
  token: string;
  qrPayload: string;
  expiresAt: string;
  state: EnrollmentState;
  capabilities: DeviceCapabilities | null;
  deviceId: string | null;
  /** SIM number the cloud learned at enrollment, if any (prefill for the member step). */
  simNumber: string | null;
}

export interface MemberProgress {
  position: number;
  number: string;
  attestedBy: string;
  jobId: string;
  job: GroupJob | undefined;
  /** Positions whose network state this job touches (predecessor + this phone). */
  affected: number[];
  /** Group snapshot once the job is done: the read-backs the page displays. */
  members: Member[] | undefined;
}

export interface HopTestProgress {
  /** Mandatory when any affected member reads back `source:"unverified"`. */
  required: boolean;
  callerNumber: string | undefined;
  jobId: string | undefined;
  result: HopTestResult | undefined;
  audit: AuditEntry | undefined;
  skipped: boolean;
  passed: boolean;
}

export interface RemovalProgress {
  group: GroupInfo;
  position: number;
  force: boolean;
  jobId: string | undefined;
  job: GroupJob | undefined;
  clearInstruction: string | undefined;
  clearedConfirmed: boolean;
  done: boolean;
}

export interface FlowError {
  code: string;
  message: string;
  step: Step;
  retryable: boolean;
}

export interface FlowState {
  v: 2;
  sessionId: string;
  step: Step;
  mode: Mode;
  business: { id: string; name: string } | undefined;
  group: GroupInfo | undefined;
  intent: Intent | undefined;
  /** Target position for this phone (1 for a new group, N+1 for add, the replaced position for replace). */
  position: number | undefined;
  device: DeviceInfo | undefined;
  model: string | undefined;
  preflight: PreflightSummary | undefined;
  /** SIM checklist (D18): must be confirmed before the phone can move past pre-flight, except for a stock restore. */
  simReady: SimReady | undefined;
  unlockConfirmed: boolean;
  /** Kept so a reload can re-verify and resume without a new fetch. */
  manifest: SignedManifestEnvelope | undefined;
  buildId: string | undefined;
  leaveUnlocked: boolean;
  flash: FlashProgress | undefined;
  enrollment: EnrollmentInfo | undefined;
  member: MemberProgress | undefined;
  hopTest: HopTestProgress | undefined;
  removal: RemovalProgress | undefined;
  error: FlowError | undefined;
  updatedAt: string;
}

export type FlowEvent =
  | { type: "SIGNED_IN"; business: { id: string; name: string } }
  | { type: "SIGNED_OUT" }
  | { type: "GROUP_SELECTED"; group: GroupInfo; intent: Intent; position: number }
  | { type: "DEVICE_READ"; device: DeviceInfo; model: string; preflight: PreflightSummary }
  | { type: "DEVICE_REFUSED"; device: DeviceInfo; preflight: PreflightSummary }
  | { type: "SIM_CONFIRMED"; sim: SimReady }
  | { type: "UNLOCK_CONFIRMED" }
  | { type: "UNLOCKED" }
  | { type: "MANIFEST_READY"; envelope: SignedManifestEnvelope; buildId: string; fileCount: number; bytesTotal: number }
  | { type: "FLASH_PHASE"; phase: FlashPhase; current?: string }
  | { type: "DOWNLOAD_PROGRESS"; fileIndex: number; current: string; bytesDone: number }
  | { type: "FLASH_PROGRESS"; fileIndex: number; current: string; partDone: number; partTotal: number }
  | { type: "LOCKED" }
  | { type: "ENROLLMENT_TOKEN"; enrollment: Omit<EnrollmentInfo, "state" | "capabilities" | "deviceId" | "simNumber"> }
  | { type: "ENROLLMENT_STATE"; state: EnrollmentState; capabilities: DeviceCapabilities | null; deviceId: string | null; simNumber?: string | null }
  | { type: "MEMBER_JOB_STARTED"; position: number; number: string; attestedBy: string; jobId: string; affected: number[] }
  | { type: "MEMBER_JOB_STATE"; job: GroupJob }
  | { type: "MEMBER_READBACK"; members: Member[] }
  | { type: "HOP_TEST_STARTED"; jobId: string; callerNumber: string }
  | { type: "HOP_TEST_STATE"; result: HopTestResult }
  | { type: "HOP_TEST_AUDIT"; entry: AuditEntry | undefined }
  | { type: "HOP_TEST_SKIPPED" }
  | { type: "INSTALL_LIVE" }
  | { type: "START_RECOVERY"; group: GroupInfo; mode: "reflash" | "stock"; leaveUnlocked: boolean; position: number | undefined }
  | { type: "START_REMOVAL"; group: GroupInfo; position: number; force: boolean }
  | { type: "REMOVAL_JOB"; jobId: string; clearInstruction?: string }
  | { type: "REMOVAL_JOB_STATE"; job: GroupJob }
  | { type: "REMOVAL_CLEARED_CONFIRMED" }
  | { type: "REMOVAL_DONE" }
  | { type: "ERROR"; code: string; message: string; retryable: boolean }
  | { type: "RETRY" }
  | { type: "RESTART_DEVICE" } // back to preflight keeping sign-in + group
  | { type: "RESET" };

export function initialState(sessionId: string, now = new Date()): FlowState {
  return {
    v: 2,
    sessionId,
    step: "signin",
    mode: "install",
    business: undefined,
    group: undefined,
    intent: undefined,
    position: undefined,
    device: undefined,
    model: undefined,
    preflight: undefined,
    simReady: undefined,
    unlockConfirmed: false,
    manifest: undefined,
    buildId: undefined,
    leaveUnlocked: false,
    flash: undefined,
    enrollment: undefined,
    member: undefined,
    hopTest: undefined,
    removal: undefined,
    error: undefined,
    updatedAt: now.toISOString(),
  };
}

export class IllegalTransition extends Error {
  constructor(state: FlowState, event: FlowEvent) {
    super(`event ${event.type} not allowed in step ${state.step}`);
    this.name = "IllegalTransition";
  }
}

const emptyFlash = (): FlashProgress => ({
  phase: "manifest",
  fileIndex: 0,
  fileCount: 0,
  bytesDone: 0,
  bytesTotal: 0,
  current: undefined,
  partDone: 0,
  partTotal: 0,
});

/** Everything device-specific, cleared when a new phone starts. */
const freshDevice = (): Pick<FlowState, "device" | "model" | "preflight" | "simReady" | "unlockConfirmed" | "manifest" | "buildId" | "flash" | "enrollment" | "member" | "hopTest" | "removal" | "error"> => ({
  device: undefined,
  model: undefined,
  preflight: undefined,
  simReady: undefined,
  unlockConfirmed: false,
  manifest: undefined,
  buildId: undefined,
  flash: undefined,
  enrollment: undefined,
  member: undefined,
  hopTest: undefined,
  removal: undefined,
  error: undefined,
});

/** True when the flow may move past pre-flight: SIM checklist confirmed, or this is a stock restore. */
export function simGateOpen(s: Pick<FlowState, "mode" | "simReady">): boolean {
  return s.mode === "stock" || s.simReady !== undefined;
}

export function reduce(s: FlowState, e: FlowEvent, now = new Date()): FlowState {
  const next = reduceInner(s, e);
  return next === s ? s : { ...next, updatedAt: now.toISOString() };
}

function reduceInner(s: FlowState, e: FlowEvent): FlowState {
  switch (e.type) {
    case "RESET":
      return { ...initialState(s.sessionId), business: s.business, step: s.business ? "group" : "signin" };
    case "SIGNED_OUT":
      return initialState(s.sessionId);
    case "ERROR":
      return { ...s, error: { code: e.code, message: e.message, step: s.step, retryable: e.retryable } };
    case "RETRY":
      return { ...s, error: undefined };
    case "RESTART_DEVICE":
      if (!s.group) throw new IllegalTransition(s, e);
      return { ...s, ...freshDevice(), step: "group", mode: "install", leaveUnlocked: false, intent: undefined, position: undefined };
    case "SIGNED_IN":
      if (s.step !== "signin") throw new IllegalTransition(s, e);
      return { ...s, business: e.business, step: "group", error: undefined };
    case "GROUP_SELECTED":
      if (s.step !== "group") throw new IllegalTransition(s, e);
      return { ...s, ...freshDevice(), group: e.group, intent: e.intent, position: e.position, mode: "install", leaveUnlocked: false, step: "preflight" };
    case "START_RECOVERY":
      if (s.step !== "group") throw new IllegalTransition(s, e);
      // Reflash re-enrolls the same phone at the same position (a replace with the same SIM).
      return { ...s, ...freshDevice(), group: e.group, mode: e.mode, leaveUnlocked: e.leaveUnlocked, intent: e.mode === "reflash" ? "replace" : undefined, position: e.position, step: "preflight" };
    case "START_REMOVAL":
      if (s.step !== "group") throw new IllegalTransition(s, e);
      return { ...s, ...freshDevice(), group: e.group, intent: undefined, position: e.position, step: "remove", removal: { group: e.group, position: e.position, force: e.force, jobId: undefined, job: undefined, clearInstruction: undefined, clearedConfirmed: false, done: false } };
    case "REMOVAL_JOB":
      if (s.step !== "remove" || !s.removal) throw new IllegalTransition(s, e);
      return { ...s, error: undefined, removal: { ...s.removal, jobId: e.jobId, clearInstruction: e.clearInstruction, job: undefined } };
    case "REMOVAL_JOB_STATE":
      if (s.step !== "remove" || !s.removal) throw new IllegalTransition(s, e);
      return { ...s, removal: { ...s.removal, job: e.job } };
    case "REMOVAL_CLEARED_CONFIRMED":
      if (s.step !== "remove" || !s.removal) throw new IllegalTransition(s, e);
      return { ...s, removal: { ...s.removal, clearedConfirmed: true } };
    case "REMOVAL_DONE":
      if (s.step !== "remove" || !s.removal || s.removal.job?.state !== "done") throw new IllegalTransition(s, e);
      if (s.removal.force && !s.removal.clearedConfirmed) throw new IllegalTransition(s, e);
      return { ...s, error: undefined, removal: { ...s.removal, done: true } };
    case "SIM_CONFIRMED":
      if (s.step !== "preflight") throw new IllegalTransition(s, e);
      return { ...s, simReady: e.sim, error: undefined };
    case "DEVICE_READ":
      // Unlock (and flash) stay unreachable until the SIM checklist is confirmed; a stock restore needs no SIM.
      if (s.step !== "preflight" || !simGateOpen(s)) throw new IllegalTransition(s, e);
      return { ...s, device: e.device, model: e.model, preflight: e.preflight, error: undefined, step: e.device.unlocked ? "flash" : "unlock", flash: e.device.unlocked ? emptyFlash() : undefined };
    case "DEVICE_REFUSED":
      if (s.step !== "preflight") throw new IllegalTransition(s, e);
      return { ...s, device: e.device, preflight: e.preflight, model: undefined, error: undefined };
    case "UNLOCK_CONFIRMED":
      if (s.step !== "unlock") throw new IllegalTransition(s, e);
      return { ...s, unlockConfirmed: true, error: undefined };
    case "UNLOCKED":
      if (s.step !== "unlock" || !s.device) throw new IllegalTransition(s, e);
      return { ...s, device: { ...s.device, unlocked: true }, step: "flash", flash: emptyFlash(), error: undefined };
    case "MANIFEST_READY":
      if (s.step !== "flash") throw new IllegalTransition(s, e);
      return { ...s, manifest: e.envelope, buildId: e.buildId, flash: { ...(s.flash ?? emptyFlash()), phase: "download", fileCount: e.fileCount, bytesTotal: e.bytesTotal, fileIndex: 0, bytesDone: 0 }, error: undefined };
    case "FLASH_PHASE":
      if (s.step !== "flash" || !s.flash) throw new IllegalTransition(s, e);
      return { ...s, flash: { ...s.flash, phase: e.phase, current: e.current, partDone: 0, partTotal: 0, ...(e.phase === "flashing" ? { fileIndex: 0 } : {}) } };
    case "DOWNLOAD_PROGRESS":
      if (s.step !== "flash" || !s.flash) throw new IllegalTransition(s, e);
      return { ...s, flash: { ...s.flash, phase: "download", fileIndex: e.fileIndex, current: e.current, bytesDone: e.bytesDone } };
    case "FLASH_PROGRESS":
      if (s.step !== "flash" || !s.flash) throw new IllegalTransition(s, e);
      return { ...s, flash: { ...s.flash, phase: "flashing", fileIndex: e.fileIndex, current: e.current, partDone: e.partDone, partTotal: e.partTotal } };
    case "LOCKED": {
      if (s.step !== "flash" || !s.flash || !s.device) throw new IllegalTransition(s, e);
      const device = { ...s.device, unlocked: s.leaveUnlocked };
      // Stock restore ends here — there is nothing to enroll.
      const step: Step = s.mode === "stock" ? "live" : "enroll";
      return { ...s, device, flash: { ...s.flash, phase: "done", current: undefined }, step, error: undefined };
    }
    case "ENROLLMENT_TOKEN":
      if (s.step !== "enroll") throw new IllegalTransition(s, e);
      return { ...s, enrollment: { ...e.enrollment, state: "pending", capabilities: null, deviceId: null, simNumber: null }, error: undefined };
    case "ENROLLMENT_STATE": {
      if ((s.step !== "enroll" && s.step !== "member" && s.step !== "hoptest") || !s.enrollment) throw new IllegalTransition(s, e);
      const enrollment: EnrollmentInfo = { ...s.enrollment, state: e.state, capabilities: e.capabilities, deviceId: e.deviceId, simNumber: e.simNumber ?? s.enrollment.simNumber };
      // Capability-probed is the gate to the member step; `live` is the cloud's final verdict
      // and now also needs the member's forwarding in sync (or a passed hop test).
      const probed = e.state === "probed" || e.state === "live";
      const step: Step = s.step === "enroll" && probed ? "member" : s.step;
      return { ...s, enrollment, step };
    }
    case "MEMBER_JOB_STARTED":
      if (s.step !== "member") throw new IllegalTransition(s, e);
      return { ...s, error: undefined, member: { position: e.position, number: e.number, attestedBy: e.attestedBy, jobId: e.jobId, job: undefined, affected: e.affected, members: undefined } };
    case "MEMBER_JOB_STATE":
      if (s.step !== "member" || !s.member) throw new IllegalTransition(s, e);
      return { ...s, member: { ...s.member, job: e.job } };
    case "MEMBER_READBACK": {
      if (s.step !== "member" || !s.member) throw new IllegalTransition(s, e);
      const affected = e.members.filter((m) => s.member!.affected.includes(m.position));
      const required = anyUnverified(affected);
      return { ...s, error: undefined, member: { ...s.member, members: e.members }, step: "hoptest", hopTest: { required, callerNumber: undefined, jobId: undefined, result: undefined, audit: undefined, skipped: false, passed: false } };
    }
    case "HOP_TEST_STARTED":
      if (s.step !== "hoptest" || !s.hopTest) throw new IllegalTransition(s, e);
      return { ...s, error: undefined, hopTest: { ...s.hopTest, jobId: e.jobId, callerNumber: e.callerNumber, result: undefined, audit: undefined, skipped: false, passed: false } };
    case "HOP_TEST_STATE":
      if (s.step !== "hoptest" || !s.hopTest) throw new IllegalTransition(s, e);
      return { ...s, hopTest: { ...s.hopTest, result: e.result, passed: hopTestPassed(e.result) } };
    case "HOP_TEST_AUDIT":
      if (s.step !== "hoptest" || !s.hopTest) throw new IllegalTransition(s, e);
      return { ...s, hopTest: { ...s.hopTest, audit: e.entry } };
    case "HOP_TEST_SKIPPED":
      if (s.step !== "hoptest" || !s.hopTest || s.hopTest.required) throw new IllegalTransition(s, e);
      return { ...s, error: undefined, hopTest: { ...s.hopTest, skipped: true } };
    case "INSTALL_LIVE":
      if (s.step !== "hoptest" || !s.hopTest || !(s.hopTest.skipped || s.hopTest.passed)) throw new IllegalTransition(s, e);
      return { ...s, error: undefined, step: "live" };
    default:
      return s;
  }
}

/** Where a persisted state should resume after a reload (device steps need a new USB grant). */
export function resumeHint(s: FlowState): "fresh" | "resume_download" | "reconnect_device" | "resume_enroll" | "resume_member" | "resume_hoptest" | "resume_remove" | "none" {
  if (s.step === "signin" || s.step === "group") return "fresh";
  if (s.step === "flash" && s.flash && (s.flash.phase === "download" || s.flash.phase === "manifest")) return "resume_download";
  if (s.step === "flash" || s.step === "unlock" || s.step === "preflight") return "reconnect_device";
  if (s.step === "enroll") return "resume_enroll";
  if (s.step === "member") return "resume_member";
  if (s.step === "hoptest") return "resume_hoptest";
  if (s.step === "remove") return "resume_remove";
  return "none";
}

export const STORAGE_KEY = "qh-installer:flow:v2";

export function loadState(storage: Pick<Storage, "getItem">): FlowState | undefined {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<FlowState>;
    if (parsed.v !== 2 || typeof parsed.sessionId !== "string" || typeof parsed.step !== "string") return undefined;
    return parsed as FlowState;
  } catch {
    return undefined;
  }
}

export function saveState(storage: Pick<Storage, "setItem" | "removeItem">, s: FlowState | undefined): void {
  if (!s) storage.removeItem(STORAGE_KEY);
  else storage.setItem(STORAGE_KEY, JSON.stringify(s));
}

/** Tiny observable store around the reducer with persistence. */
export class FlowStore {
  private s: FlowState;
  private readonly listeners = new Set<(s: FlowState) => void>();

  constructor(
    initial: FlowState,
    private readonly storage?: Pick<Storage, "setItem" | "removeItem">,
    private readonly now: () => Date = () => new Date(),
  ) {
    this.s = initial;
  }
  get state(): FlowState {
    return this.s;
  }
  dispatch(e: FlowEvent): FlowState {
    this.s = reduce(this.s, e, this.now());
    if (this.storage) saveState(this.storage, this.s);
    for (const l of this.listeners) l(this.s);
    return this.s;
  }
  subscribe(l: (s: FlowState) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
}
